import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "../../../../features/AppState";
import { upsertFetcherConfig } from "../../../../features/fetcherSettingsSlice";
import { setDisplayErrorSnackBar } from "../../../../features/userSlice";
import { configTwins, lookupName } from "../helpers/fetcherSettingsResolver";
import { formatDisplayDateTime, nowStamp } from "../helpers/formatters";
import ConfigDefinitionFields, { ConfigDraft } from "./ConfigDefinitionFields";
import CreateReportModal from "../Reports/CreateReportModal";
import SaveChangesReviewModal, {
  FieldChange,
} from "../SaveChangesReviewModal";

interface Props {
  open: boolean;
  configId: string;
  mode: "view" | "edit";
  onClose: () => void;
  onSwitchToEdit: () => void;
  onDuplicate: (configId: string) => void;
  onShowBrands: (configId: string) => void;
}

const EditConfigModal = ({
  open,
  configId,
  mode,
  onClose,
  onSwitchToEdit,
  onDuplicate,
  onShowBrands,
}: Props) => {
  const dispatch = useDispatch();
  const {
    configs,
    reports,
    interfaces,
    collectionMethods,
    subFilteringMethods,
    formatTypes,
  } = useSelector((state: AppState) => state.fetcherSettings);

  const config = configs.find((row) => row.id === configId);

  const [draft, setDraft] = useState<ConfigDraft | null>(null);
  const [isReportDialogOpen, setIsReportDialogOpen] = useState(false);
  const [isReviewOpen, setIsReviewOpen] = useState(false);

  useEffect(() => {
    if (!open || !config) return;
    setDraft({
      name: config.name,
      report: config.report,
      cm: config.cm,
      sfm: config.sfm,
      ft: config.ft,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, configId]);

  const isReadOnly = mode === "view";

  const isDirty = useMemo(() => {
    if (!config || !draft) return false;
    return (
      draft.name !== config.name ||
      draft.report !== config.report ||
      draft.cm !== config.cm ||
      draft.sfm !== config.sfm ||
      draft.ft !== config.ft
    );
  }, [config, draft]);

  if (!config || !draft) return null;

  const backendInterface = interfaces.find((row) => row.id === config.iface);
  const clashes = configTwins(configs, {
    ...config,
    cm: draft.cm,
    sfm: draft.sfm,
    ft: draft.ft,
    report: draft.report,
  });
  const recipeChanged =
    draft.cm !== config.cm ||
    draft.sfm !== config.sfm ||
    draft.ft !== config.ft;

  const fieldChanges: FieldChange[] = [];
  if (draft.name !== config.name) {
    fieldChanges.push({ label: "Name", from: config.name, to: draft.name });
  }
  if (draft.report !== config.report) {
    const before = reports.find((row) => row.id === config.report);
    const after = reports.find((row) => row.id === draft.report);
    fieldChanges.push({
      label: "Report",
      from: before ? `${before.name} (${before.id})` : "-",
      to: after ? `${after.name} (${after.id})` : "-",
    });
  }
  if (draft.cm !== config.cm) {
    fieldChanges.push({
      label: "Collection method",
      from: lookupName(collectionMethods, config.cm),
      to: lookupName(collectionMethods, draft.cm),
    });
  }
  if (draft.sfm !== config.sfm) {
    fieldChanges.push({
      label: "Sub-filtering",
      from: lookupName(subFilteringMethods, config.sfm),
      to: lookupName(subFilteringMethods, draft.sfm),
    });
  }
  if (draft.ft !== config.ft) {
    fieldChanges.push({
      label: "Format type",
      from: lookupName(formatTypes, config.ft),
      to: lookupName(formatTypes, draft.ft),
    });
  }

  const handleSave = () => {
    // TODO: PUT /aladdin_api/v1/data_collection/fetcher_config/<shared_uid>
    const report = reports.find((row) => row.id === draft.report);
    dispatch(
      upsertFetcherConfig({
        ...config,
        name: draft.name.trim(),
        report: draft.report,
        report_name: report?.name ?? config.report_name,
        iface: report?.iface ?? config.iface,
        ctype: report?.ctype ?? config.ctype,
        cm: draft.cm,
        sfm: draft.sfm,
        ft: draft.ft,
        ftn: lookupName(formatTypes, draft.ft),
        mod: nowStamp(),
      }),
    );
    dispatch(
      setDisplayErrorSnackBar({
        message: `${draft.name.trim()} saved.`,
        type: "success",
      }),
    );
    setIsReviewOpen(false);
    onClose();
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        PaperProps={{ sx: { width: "880px", maxWidth: "94vw" } }}
      >
        <DialogTitle sx={{ pb: 0.5 }}>
          {isReadOnly ? "View Config" : "Edit Config"}
        </DialogTitle>
        <Typography
          variant="body2"
          sx={{ px: 3, pb: 1.5, color: "#00000099" }}
        >
          {config.name} ({config.id}) · modified{" "}
          {formatDisplayDateTime(config.mod)}
        </Typography>
        <Divider />

        <Stack
          direction="row"
          alignItems="center"
          spacing={2}
          sx={{
            px: 3,
            py: 1.5,
            backgroundColor: "#FAFAFC",
            borderBottom: "1px solid #0000001F",
          }}
        >
          <Box>
            <Typography sx={{ fontSize: "18px", fontWeight: 500 }}>
              {config.used}
            </Typography>
            {config.used > 0 ? (
              <Link
                component="button"
                underline="always"
                color="secondary"
                sx={{ fontSize: "12px" }}
                onClick={() => onShowBrands(config.id)}
              >
                brands - view list
              </Link>
            ) : (
              <Typography sx={{ fontSize: "12px", color: "#00000099" }}>
                brands
              </Typography>
            )}
          </Box>
          <Divider orientation="vertical" flexItem />
          <Box>
            <Typography sx={{ fontSize: "12px", color: "#00000099" }}>
              Interface
            </Typography>
            <Chip size="small" label={backendInterface?.name ?? "-"} />
          </Box>
          <Divider orientation="vertical" flexItem />
          <Box>
            <Typography sx={{ fontSize: "12px", color: "#00000099" }}>
              Report
            </Typography>
            <Typography sx={{ fontSize: "13.5px" }}>
              {config.report_name || "-"}
            </Typography>
          </Box>
        </Stack>

        <DialogContent sx={{ px: 3, py: 2 }}>
          {clashes.length > 0 && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <b>Blocked - 100% duplicate.</b>{" "}
              {clashes.map((row) => `${row.name} (${row.id})`).join(", ")}{" "}
              already has this exact recipe.
            </Alert>
          )}
          {recipeChanged && config.used > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Changing the recipe changes collection for{" "}
              <b>all {config.used} brands</b> on the next run.
            </Alert>
          )}

          <ConfigDefinitionFields
            draft={draft}
            onChange={setDraft}
            canPickReport={false}
            readOnly={isReadOnly}
            onCreateReport={() => setIsReportDialogOpen(true)}
          />

          {!isReadOnly && (
            <Typography sx={{ fontSize: "12px", color: "#00000099", mt: 1.5 }}>
              <b>Create new report</b> adds one to the catalogue; to run this
              recipe on it, duplicate the config.
            </Typography>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          {isReadOnly ? (
            <>
              <Chip size="small" label="Read only" />
              <Box sx={{ flex: 1 }} />
              <Button color="secondary" onClick={onClose}>
                Close
              </Button>
              <Button
                color="secondary"
                onClick={() => onDuplicate(config.id)}
              >
                Duplicate
              </Button>
              <Button
                variant="contained"
                color="secondary"
                onClick={onSwitchToEdit}
              >
                Edit
              </Button>
            </>
          ) : (
            <>
              {isDirty && (
                <Typography sx={{ fontSize: "12.5px", color: "#B45309" }}>
                  ● unsaved changes
                </Typography>
              )}
              <Box sx={{ flex: 1 }} />
              {isDirty && (
                <Button
                  color="secondary"
                  onClick={() =>
                    setDraft({
                      name: config.name,
                      report: config.report,
                      cm: config.cm,
                      sfm: config.sfm,
                      ft: config.ft,
                    })
                  }
                >
                  Discard
                </Button>
              )}
              <Button color="secondary" onClick={onClose}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color="secondary"
                disabled={!isDirty || clashes.length > 0}
                onClick={() => setIsReviewOpen(true)}
              >
                Save changes
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <CreateReportModal
        open={isReportDialogOpen}
        onClose={() => setIsReportDialogOpen(false)}
        onCreated={() => setIsReportDialogOpen(false)}
      />

      <SaveChangesReviewModal
        open={isReviewOpen}
        entityLabel="Config"
        itemName={config.name}
        fieldChanges={fieldChanges}
        objectChanges={[]}
        affectedCount={config.used}
        affectedDescription="brands collecting with this config"
        dependants={config.brands.map((brand) => ({
          primary: brand.n,
          secondary: `raw_data_${brand.rt}`,
        }))}
        onClose={() => setIsReviewOpen(false)}
        onConfirm={handleSave}
      />
    </>
  );
};

export default EditConfigModal;
